// Shared API helper for talking to the new Hiilt gateway (user-service auth +
// manasa-service). Replaces the old flat fetch()-per-page pattern that called
// Payara directly: every manasa-service response is now an ApiResponse
// envelope ({success, payload, error}), and every request (except login)
// needs "Authorization: Bearer <token>".

import { startUpload } from "./UploadProgress";

const properties = require("../properties.json");

const TOKEN_KEY = "manasa_token";
const USER_KEY = "manasa_user";

export function getToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser() {
  const raw = window.localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function isLoggedIn() {
  return !!getToken();
}

export function saveSession(token, user) {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

function buildQuery(query) {
  if (!query) return "";
  const parts = Object.entries(query)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * Calls manasa-service through the gateway. `path` is relative to
 * properties.api_path, e.g. "/lessons" or "/lookup/governorates".
 * Unwraps the ApiResponse envelope and returns just the payload; redirects
 * to the login page on 401 (expired/missing token).
 */
export async function apiFetch(path, { method = "GET", body, query } = {}) {
  const url = properties.api_path + path + buildQuery(query);
  const headers = { Accept: "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearSession();
    window.location.href = "/";
    throw new Error("Unauthorized");
  }

  if (res.status === 204) return null;

  const json = await res.json();
  if (!json.success) {
    throw new Error((json.error && json.error.message) || "حدث خطأ غير متوقع");
  }
  return json.payload;
}

// Saved file links (url) are not publicly readable except images; right after an upload the server also returns a
// short-lived previewUrl, remembered here so the page can open the file before it is saved and reloaded.
const previewLinks = new Map();

/** The link to open for a file: the fresh preview of a just-uploaded file, else the link as given (links coming
 *  from the API are already signed). */
export function previewLink(url) {
  return previewLinks.get(url) || url;
}

/** Thrown by uploadCourseMedia when the caller cancels an in-progress upload via the xhr handed
 *  to onXhrReady -- callers check `error.cancelled` to skip showing an error toast for a
 *  deliberate cancel. */
export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled");
    this.cancelled = true;
  }
}

/**
 * Uploads a real file (course-item video/audio/image/document) to Contabo Object Storage via
 * manasa-service and returns its public URL. Multipart, so it bypasses apiFetch's JSON body.
 */
// onProgress, if given, is called with an integer 0-100 as the upload advances. Uses
// XMLHttpRequest instead of fetch specifically for this -- fetch has no way to observe
// request-body (upload) progress, only response-download progress, which is useless here since
// the response is a tiny JSON payload.
// onXhrReady, if given, is called synchronously with the XMLHttpRequest before it's sent, so the
// caller can stash it and later call xhr.abort() to cancel -- that rejects this promise with an
// UploadCancelledError instead of leaving the caller with no way to stop a large in-flight upload.
function uploadMultipart(path, file, onProgress, onXhrReady) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", properties.api_path + path);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
    if (onXhrReady) onXhrReady(xhr);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      if (xhr.status === 401) {
        clearSession();
        window.location.href = "/";
        reject(new Error("Unauthorized"));
        return;
      }
      if (xhr.status === 413) {
        reject(new Error("الملف كبير جداً على السيرفر أن يستقبله"));
        return;
      }
      let json;
      try {
        json = JSON.parse(xhr.responseText);
      } catch (e) {
        reject(new Error(`فشل رفع الملف (خطأ ${xhr.status})`));
        return;
      }
      if (!json.success) {
        reject(new Error((json.error && json.error.message) || "فشل رفع الملف"));
        return;
      }
      if (json.payload.previewUrl) previewLinks.set(json.payload.url, json.payload.previewUrl);
      resolve(json.payload.url);
    };
    xhr.onerror = () => reject(new Error("خطأ في الإتصال أثناء رفع الملف"));
    xhr.onabort = () => reject(new UploadCancelledError());
    xhr.send(formData);
  });
}

// Same list as manasa-service's CourseMediaService#isVideo -- a video counts against the reserved videos.
const VIDEO_EXTENSIONS = /\.(mp4|m4v|mov|mkv|webm|avi|wmv|flv|3gp|mpeg|mpg|ts)$/i;

export function isVideoFile(file) {
  return !!file && ((file.type || "").toLowerCase().startsWith("video/") || VIDEO_EXTENSIONS.test(file.name || ""));
}

/** Asks the server whether this file fits (storage left, and a video place for a video) BEFORE sending a byte,
 *  so a big file is refused in a second instead of after a long upload. The server checks again on upload; if
 *  this question itself fails (network, old server) the upload just goes ahead. */
async function precheckUpload(file, video) {
  if (!file) return;
  let answer;
  try {
    answer = await apiFetch("/billing/upload-check", { query: { bytes: file.size, video } });
  } catch (e) {
    return;
  }
  if (answer && !answer.allowed) throw new Error(answer.message || "لا توجد مساحة كافية لهذا الملف");
}

// Uploads without their own progress callback get the floating progress panel (see UploadProgress.js).
async function uploadWithProgress(path, file, onProgress, onXhrReady, video) {
  await precheckUpload(file, video);
  if (onProgress) return uploadMultipart(path, file, onProgress, onXhrReady);
  const ui = startUpload(file && file.name);
  return uploadMultipart(path, file, (pct) => ui.update(pct), onXhrReady).then(
    (url) => { ui.finish(true); return url; },
    (err) => { ui.finish(false); throw err; }
  );
}

/** context {courseId, materialId} (optional) only picks the teacher's storage sub-folder (stage / subject). */
export function uploadCourseMedia(file, onProgress, onXhrReady, context) {
  const params = new URLSearchParams();
  if (context && context.courseId) params.set("courseId", context.courseId);
  if (context && context.materialId) params.set("materialId", context.materialId);
  const query = params.toString();
  return uploadWithProgress("/course-media/upload" + (query ? "?" + query : ""), file, onProgress, onXhrReady, isVideoFile(file));
}

/** Uploads one site image (logo, hero, about photo, ...) and resolves to its public URL. */
export function uploadBrandingImage(file, onProgress, onXhrReady) {
  return uploadWithProgress("/teachers/me/branding/upload", file, onProgress, onXhrReady, false);
}

/** POSTs a multipart form (fields + optional files) to manasa-service and unwraps the ApiResponse envelope. */
export async function apiForm(path, formData) {
  const headers = { Accept: "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(properties.api_path + path, { method: "POST", headers, body: formData });
  if (res.status === 401) {
    clearSession();
    window.location.href = "/";
    throw new Error("Unauthorized");
  }
  if (res.status === 413) throw new Error("الملف كبير جداً");
  const json = await res.json().catch(() => ({}));
  if (!json.success) throw new Error((json.error && json.error.message) || "حدث خطأ غير متوقع");
  return json.payload;
}

/** Calls user-service's login endpoint (not gateway-routed under /api/manasa). */
export async function login(email, password) {
  const res = await fetch(properties.auth_path + "/login", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error((json.error && json.error.message) || "بيانات الدخول غير صحيحة");
  }
  return json.payload; // { token, userId, email, fullName, role }
}

/** Teachers still log in with their old short user_code; the migration gave
 *  every teacher a synthesized "<user_code>@manasa.local" email. */
export function toLoginEmail(usernameOrEmail) {
  const value = (usernameOrEmail || "").trim();
  return value.includes("@") ? value : `${value}@manasa.local`;
}

/** The platform facts for the dashboard host (its category decides the base domain and modules). */
export function platformInfo() {
  return apiFetch("/lookup/platform-info", { query: { host: window.location.hostname } });
}
